import { h } from 'preact';
import { useCallback } from 'preact/hooks';

type PlanPhaseStatus = 'pending' | 'executing' | 'awaiting-merge' | 'completed' | 'failed' | 'awaiting-deploy' | 'blocked';
type WaveStatus = 'computing' | 'executing' | 'paused' | 'completed' | 'failed';

interface PlanPhaseRef {
  planId: string;
  planName: string;
  projectKey: string;
  phaseNumber: number;
  branch?: string;
  prNumber?: number;
  mergeCommitSha?: string;
  blockReason?: string;
  status: PlanPhaseStatus;
}

interface WaveDefinition {
  index: number;
  label: string;
  planPhases: PlanPhaseRef[];
  status: 'pending' | 'executing' | 'completed' | 'failed' | 'awaiting-deploy';
}

interface WaveExecution {
  id: string;
  status: WaveStatus;
  waves: WaveDefinition[];
  currentWaveIndex: number;
  conflictMatrix: Array<{ planIdA: string; phaseA: number; planIdB: string; phaseB: number; sharedFiles: string[] }>;
  pauseReason?: string | null;
  completedAt?: string | null;
  lighthouseSessionId?: string | null;
  /** Heartbeat timestamp — bumped by coordinator on every advancement. ISO string. */
  lastProgressAt?: string | null;
}

/** Threshold beyond which the UI shows an amber "stale heartbeat" warning chip. */
const STALE_HEARTBEAT_WARNING_MS = 30 * 60 * 1000; // 30 min

interface Props {
  wave: WaveExecution;
  onClose: () => void;
  onRefresh: () => void;
}

const STATUS_BADGE: Record<string, string> = {
  pending: 'plans-wave-badge--pending',
  executing: 'plans-wave-badge--executing',
  'awaiting-merge': 'plans-wave-badge--await',
  'awaiting-deploy': 'plans-wave-badge--deploy',
  completed: 'plans-wave-badge--done',
  failed: 'plans-wave-badge--fail',
  blocked: 'plans-wave-badge--blocked',
  computing: 'plans-wave-badge--pending',
  paused: 'plans-wave-badge--await',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  executing: '⚙ Running',
  'awaiting-merge': '⏳ Awaiting merge',
  'awaiting-deploy': '🚀 Awaiting deploy',
  completed: '✓ Done',
  failed: '✗ Failed',
  blocked: '⚠ Blocked',
  computing: '⚙ Computing',
  paused: '⏸ Paused',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span class={`plans-wave-badge ${STATUS_BADGE[status] ?? 'plans-wave-badge--pending'}`}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

export function WaveProgressPanel({ wave, onClose, onRefresh }: Props) {
  const handlePause = useCallback(async () => {
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch(`/api/plans/wave/${wave.id}/pause`, { method: 'POST' });
      onRefresh();
    } catch { /* ignore */ }
  }, [wave.id, onRefresh]);

  const handleResume = useCallback(async () => {
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch(`/api/plans/wave/${wave.id}/resume`, { method: 'POST' });
      onRefresh();
    } catch { /* ignore */ }
  }, [wave.id, onRefresh]);

  const handleSkipDeployGate = useCallback(async () => {
    try {
      const { authFetch } = await import('../api/authFetch');
      await authFetch(`/api/plans/wave/${wave.id}/skip-deploy-gate`, { method: 'POST' });
      onRefresh();
    } catch { /* ignore */ }
  }, [wave.id, onRefresh]);

  const isActive = wave.status === 'executing' || wave.status === 'computing';
  const isPaused = wave.status === 'paused';
  const isAwaitingDeploy = wave.pauseReason?.startsWith('deploy-gate:');
  const heartbeatTimedOut = wave.status === 'failed' && (wave.pauseReason ?? '').toLowerCase().includes('heartbeat timeout');
  const heartbeatStale = (() => {
    if (!isActive || !wave.lastProgressAt) return false;
    const last = new Date(wave.lastProgressAt).getTime();
    if (!Number.isFinite(last)) return false;
    return Date.now() - last > STALE_HEARTBEAT_WARNING_MS;
  })();
  const heartbeatAgeMin = wave.lastProgressAt
    ? Math.round((Date.now() - new Date(wave.lastProgressAt).getTime()) / 60_000)
    : null;

  return (
    <div class="plans-wave-panel">
      <div class="plans-wave-panel-header">
        <span class="plans-wave-panel-title">Wave Execution</span>
        <StatusBadge status={wave.status} />
        {heartbeatStale && !heartbeatTimedOut && (
          <span
            class="plans-wave-badge plans-wave-badge--stale"
            title={`No coordinator progress for ${heartbeatAgeMin}m — wave may be stuck. Auto-fail at 90m.`}
          >
            ⚠ Stale ({heartbeatAgeMin}m)
          </span>
        )}
        {heartbeatTimedOut && (
          <span
            class="plans-wave-badge plans-wave-badge--heartbeat-timeout"
            title="Heartbeat timed out — wave was auto-failed by safety net. Use force-advance if recoverable."
          >
            🛑 Heartbeat timeout
          </span>
        )}
        <div class="plans-wave-panel-actions">
          {isActive && (
            <button class="plans-wave-ctrl-btn" onClick={handlePause} title="Pause wave">⏸</button>
          )}
          {isPaused && !isAwaitingDeploy && (
            <button class="plans-wave-ctrl-btn plans-wave-ctrl-btn--resume" onClick={handleResume} title="Resume wave">▶</button>
          )}
          {isAwaitingDeploy && (
            <button class="plans-wave-ctrl-btn plans-wave-ctrl-btn--skip-gate" onClick={handleSkipDeployGate} title="Skip deploy gate">⏭ Skip Gate</button>
          )}
          {wave.lighthouseSessionId && (
            <button
              class="plans-wave-ctrl-btn plans-wave-ctrl-btn--lighthouse"
              title="View lighthouse session"
              onClick={() => window.dispatchEvent(new CustomEvent('valko-open-session', { detail: { sessionId: wave.lighthouseSessionId } }))}
            >
              🔦
            </button>
          )}
          <button class="plans-wave-close-btn" onClick={onClose} title="Dismiss panel">✕</button>
        </div>
      </div>

      {isAwaitingDeploy && (
        <div class="plans-wave-deploy-banner">
          🚀 Awaiting deploy — merge is complete, waiting for production deploy to continue.
        </div>
      )}

      {wave.pauseReason && !isAwaitingDeploy && (
        <div class="plans-wave-pause-reason">
          <strong>Paused:</strong> {wave.pauseReason.slice(0, 200)}
        </div>
      )}

      {wave.status === 'completed' && (
        <div class="plans-wave-complete-banner">All waves complete!</div>
      )}

      <div class="plans-wave-timeline">
        {(wave.waves ?? []).map((wv, i) => {
          const isCurrent = i === wave.currentWaveIndex && wave.status !== 'completed';
          return (
            <div
              key={i}
              class={`plans-wave-row${isCurrent ? ' plans-wave-row--current' : ''}${wv.status === 'completed' ? ' plans-wave-row--done' : ''}`}
            >
              <div class="plans-wave-row-header">
                <span class="plans-wave-row-label">{wv.label}</span>
                <StatusBadge status={wv.status} />
              </div>
              <div class="plans-wave-phase-list">
                {wv.planPhases.map((pp, j) => (
                  <div
                    key={j}
                    class={`plans-wave-phase-card${pp.status === 'awaiting-deploy' ? ' plans-wave-phase-card--deploy' : ''}${pp.status === 'blocked' ? ' plans-wave-phase-card--blocked' : ''}`}
                    title={pp.blockReason ?? undefined}
                  >
                    <span class="plans-wave-phase-name">{pp.planName}</span>
                    <span class="plans-wave-phase-project">{pp.projectKey}</span>
                    <StatusBadge status={pp.status} />
                    {pp.prNumber && (
                      <span class="plans-wave-pr-link">PR #{pp.prNumber}</span>
                    )}
                    {pp.status === 'awaiting-deploy' && pp.mergeCommitSha && (
                      <span class="plans-wave-phase-sha" title={pp.mergeCommitSha}>
                        SHA {pp.mergeCommitSha.slice(0, 7)}
                      </span>
                    )}
                    {pp.blockReason && (
                      <span class="plans-wave-block-reason">{pp.blockReason}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {wave.conflictMatrix?.length > 0 && (
        <details class="plans-wave-conflicts">
          <summary>Conflict matrix ({wave.conflictMatrix.length} entries)</summary>
          <div class="plans-wave-conflict-list">
            {wave.conflictMatrix.slice(0, 10).map((c, i) => (
              <div key={i} class="plans-wave-conflict-entry">
                <span class="plans-wave-conflict-plans">{c.planIdA.slice(0, 8)} ↔ {c.planIdB.slice(0, 8)}</span>
                <span class="plans-wave-conflict-files">{c.sharedFiles.join(', ')}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
